import { useState } from "react";
import { X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

/** The rule core applies (`normaliseAreas`), so a chip that shows is one the server keeps. */
const AREA = /^[a-z0-9][a-z0-9_./-]{0,39}$/;
const MAX_AREAS = 12;

/** A card's areas as chips, read-only. */
export function AreaChips({ areas }: { areas: string[] }) {
  if (areas.length === 0) return <span className="text-muted-foreground">None</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {areas.map((area) => (
        <Badge key={area} variant="outline" className="font-mono">
          {area}
        </Badge>
      ))}
    </span>
  );
}

/**
 * Areas as removable chips plus a box that adds one on Enter or comma. Each
 * change saves the whole list, since the server replaces it.
 */
export function AreasEditor({ areas, onChange }: { areas: string[]; onChange: (areas: string[]) => void }) {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  const add = () => {
    const names = draft
      .split(",")
      .map((name) => name.trim().toLowerCase())
      .filter(Boolean);
    if (names.length === 0) return;
    const bad = names.find((name) => !AREA.test(name));
    if (bad) return setError(`"${bad}": 1–40 characters of a-z, 0-9, _ . / or -`);
    const next = [...new Set([...areas, ...names])];
    if (next.length > MAX_AREAS) return setError(`At most ${MAX_AREAS} areas per card`);
    setError(null);
    setDraft("");
    if (next.length !== areas.length) onChange(next);
  };

  return (
    <div className="space-y-1.5">
      {areas.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {areas.map((area) => (
            <Badge key={area} variant="outline" className="gap-0.5 pr-1 font-mono">
              {area}
              <button
                type="button"
                className="rounded-full p-0.5 hover:bg-muted"
                aria-label={`Remove ${area}`}
                onClick={() => onChange(areas.filter((other) => other !== area))}
              >
                <X />
              </button>
            </Badge>
          ))}
        </div>
      ) : null}
      <Input
        value={draft}
        placeholder="db, web, worker…"
        onChange={(event) => {
          setDraft(event.target.value);
          setError(null);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === ",") {
            event.preventDefault();
            add();
          }
        }}
        onBlur={add}
        aria-label="Add an area"
        className="h-8 text-[13px]"
      />
      <p className="text-[11px] leading-snug text-muted-foreground">
        {error ?? "Parts of the codebase this card touches. An agent running cards in parallel never works two cards that share one at once."}
      </p>
    </div>
  );
}
